/**
 * `<GmDirectivePanel>` — the DIRECTOR rail page: the GM's surface for what is on the players'
 * screens, the browser counterpart to `lvtt directive <verb>`. Four parts, top to bottom, the most
 * used first:
 *  - **ON PLAYER SCREENS NOW** — the live banner (with CLEAR) and the table's scene, live or over.
 *  - **01 SCENE** — the table's scene card over a PUT ON TABLE radio list of narrative + every
 *    encounter, through the shell's `putOnTable`. On a fight, END COMBAT opens COMBAT's own END
 *    COMBAT panel (commit wear and end, or end without committing); ending puts the table on
 *    Narrative. ENDED FIGHTS lists every other ended fight with REOPEN (the server puts a reopened
 *    fight back on the table, so players never see the over board) and, while its wear is off the
 *    roster, COMMIT WEAR (the same panel as a commit-only gate). An ended fight shown on the table
 *    gets REOPEN COMBAT and COMMIT WEAR on its card.
 *  - **02 TRANSMISSION** — a banner in a tone (OMNINET / ALERT / NARRATION) with a live preview →
 *    `session.setView`: DURABLE (`game_set_view`), persisted and re-shown on a player's relink.
 *  - **03 CUES** — GLITCH / IMPACT / SHAKE → `session.sendFx`: EPHEMERAL, fire-and-forget
 *    (`game_fx`), never persisted.
 *
 * Transmissions persist on the PLAYER slots only. The GM sees what the players see: the GM's
 * overlay mirrors the players' banner (`GameSessionProvider`), and a cue is fired at the players
 * and the GM's own slot together.
 *
 * GM-gated like the COMBAT.app authoring controls: it renders nothing for a non-GM. That's the
 * affordance — the server's `GameRoom.handle` gate is the real wall, and `sendFx`/`setView`
 * themselves no-op unless `role === "gm"`. The directive shapes, copy and scene card live in the
 * pure `gmDirectives` / `sceneStatus` modules so they're unit-tested without a render harness.
 */
import { useEffect, useState } from "react";
import { bannerTone, GRID_MODES, type BannerTone, type DirectiveTarget, type EncounterSummary } from "@lancer-vtt/shared";
import { NARRATIVE_SCENE, encounterScene, type TableScene } from "@lancer-vtt/domain";
import { listEncounters } from "../../lib/api";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import { NotSentNote, useNotSentNote } from "../../lib/linkHealth";
import {
  CUES,
  SAMPLE_TRANSMISSION,
  TONES,
  bannerPatch,
  canDirect,
  clearBannerPatch,
  cueSentLine,
  fxDirective,
  type Cue,
} from "../../lib/game/gmDirectives";
import { sceneCard, wearUncommitted, type FightStatus } from "../../lib/game/sceneStatus";
import { EndCombatPanel } from "./combatApp/EndCombatPanel";
import { BackdropSceneRow } from "./shell/backdrop/BackdropSceneRow";
import { useStage } from "./shell/stageContext";
import { NO_FIGHT, useSceneFight, type SceneFight } from "./useSceneFight";
import "./director.css";

const GRID_LABEL = new Map(GRID_MODES.map((g) => [g.value, g.label]));

/** The GM's unsent transmission and last cue, held above the scene swap so neither is lost. */
type Draft = {
  text: string;
  setText: (text: string) => void;
  tone: BannerTone;
  setTone: (tone: BannerTone) => void;
  fired: string | null;
  setFired: (line: string) => void;
};

export function GmDirectivePanel({ roster }: { roster: UseRoster }) {
  const session = useGameSessionContext();
  const [text, setText] = useState("");
  const [tone, setTone] = useState<BannerTone>("omninet");
  const [fired, setFired] = useState<string | null>(null);
  // GM-gate: this whole surface is GM-only — it simply doesn't render for a non-GM.
  if (!canDirect(session.role)) return null;
  const draft: Draft = { text, setText, tone, setTone, fired, setFired };
  const scene = session.tableScene;
  // A fight on the table gets an encounter socket of its own, mounted per encounter.
  return scene.kind === "encounter" ? (
    <SceneFightReader key={scene.encounterId} encounterId={scene.encounterId} draft={draft} roster={roster} />
  ) : (
    <Director fight={NO_FIGHT} draft={draft} roster={roster} />
  );
}

function SceneFightReader({ encounterId, draft, roster }: { encounterId: string; draft: Draft; roster: UseRoster }) {
  return <Director fight={useSceneFight(encounterId)} draft={draft} roster={roster} />;
}

function Director({ fight, draft, roster }: { fight: SceneFight; draft: Draft; roster: UseRoster }) {
  const session = useGameSessionContext();
  const { compendium } = useCompendiumContext();
  const [endOpen, setEndOpen] = useState(false);
  // An ended fight's REOPEN or COMMIT WEAR, run over its own encounter socket (`EndedFightAct`).
  const [act, setAct] = useState<EndedAct | null>(null);
  const stage = useStage();
  const { notSent, report } = useNotSentNote();
  const { text, setText, tone, setTone, fired, setFired } = draft;
  const [encounters, setEncounters] = useState<EncounterSummary[]>([]);
  const scene = session.tableScene;

  // The PUT ON TABLE list, read on mount (and so again on every scene change, which remounts).
  useEffect(() => {
    void listEncounters().then((listed) => {
      if (listed.ok) setEncounters(listed.value);
    });
  }, []);

  const players = session.slots.filter((s) => s.role === "player");
  const target: DirectiveTarget = { slotIds: players.map((s) => s.id) };
  // A cue plays on every seat, the GM's own included, so the GM sees what the players see.
  const cueTarget: DirectiveTarget = { slotIds: session.slots.map((s) => s.id) };
  const live = session.playerBanner;
  const trimmed = text.trim();

  const fire = (cue: Cue) => {
    const sent = session.sendFx(fxDirective(cue.type), cueTarget);
    report(sent);
    if (sent) setFired(cueSentLine(cue.label, players.filter((p) => p.connected).length));
  };
  const transmit = () => {
    if (trimmed === "") return;
    report(session.setView(bannerPatch(trimmed, tone), target));
  };
  const clear = () => report(session.setView(clearBannerPatch(), target));
  const putOnTable = (next: TableScene) => report(stage ? stage.putOnTable(next) : session.setTableScene(next));

  const sceneName = (s: TableScene) =>
    s.kind === "narrative" ? "Narrative" : (encounters.find((e) => e.id === s.encounterId)?.name ?? "Unnamed encounter");
  const card = sceneCard({ scene, name: sceneName(scene), fight: fight.status });
  const tableId = sceneEncounterIdOf(scene);
  const owes = (id: string) => {
    const enc = encounters.find((e) => e.id === id);
    return enc !== undefined && wearUncommitted({ encounter: enc, roster: roster.state });
  };
  // A fight shown on the table owes its wear by its live state; the list's stamp may predate it.
  const tableOwes =
    tableId !== null && fight.status?.over === true && roster.state !== null && roster.state.encounterCommits?.[tableId] === undefined;
  const ended = encounters.filter((e) => e.combatEndedAt !== undefined && e.id !== tableId);
  const liveFight = card.isCombat && card.state === "LIVE";

  return (
    <div className="dir">
      <section className="dir-now" aria-label="On player screens now">
        <div className="dir-now-label">ON PLAYER SCREENS NOW</div>
        <div className="dir-now-row" data-testid="director-now-banner">
          <div className="dir-now-key">BANNER</div>
          {live ? (
            <>
              <div className="dir-now-val">
                <span className="dir-dot dir-dot-blink" />
                <span className="dir-now-text">{live.text}</span>
              </div>
              <button type="button" className="dir-btn-ghost" onClick={clear}>
                CLEAR
              </button>
            </>
          ) : (
            <>
              <div className="dir-now-none">None</div>
              <div />
            </>
          )}
        </div>
        <div className="dir-now-row" data-testid="director-now-scene">
          <div className="dir-now-key">SCENE</div>
          <div className="dir-now-val">
            <span className={`dir-dot${liveFight ? "" : " is-idle"}`} />
            <span className="dir-now-text">{card.line}</span>
          </div>
          <div />
        </div>
      </section>

      <section className="dir-sec" aria-label="Scene">
        <SectionHead num="01" title="SCENE" note="What the table is looking at." />
        <div className="dir-card" data-testid="director-scene-card" data-state={card.state}>
          <div className="dir-card-head">
            <span className="dir-card-kind">{card.kind}</span>
            <span className={`dir-card-state${liveFight ? " is-live" : ""}`}>
              <span className={`dir-dot${liveFight ? "" : " is-idle"}`} />
              {card.state}
            </span>
          </div>
          <div className="dir-card-body">
            <span className="dir-card-name">{card.name}</span>
            <span className="dir-card-detail">{card.detail}</span>
          </div>
          {card.isCombat && (
            <CombatControls
              fight={fight.status}
              onEnd={() => setEndOpen(true)}
              reopen={() => report(fight.reopen().ok)}
              commitWear={tableOwes ? () => setAct({ id: tableId!, kind: "wear" }) : undefined}
            />
          )}
        </div>
        {card.isCombat && card.state === "OVER" && <div className="dir-over-note">This fight is over. Turns and actions are locked.</div>}
        <BackdropSceneRow />

        <div className="dir-field dir-field-gap">PUT ON TABLE</div>
        <div role="radiogroup" aria-label="Scene on the table" className="dir-scenes">
          <SceneRow scene={NARRATIVE_SCENE} current={scene} name="Narrative" sub="No map. Talk, downtime, briefings." kind="TALK" onPick={putOnTable} />
          {encounters.map((e) => (
            <SceneRow key={e.id} scene={encounterScene(e.id)} current={scene} name={e.name || "Unnamed encounter"} sub={GRID_LABEL.get(e.gridMode) ?? e.gridMode} kind="COMBAT" onPick={putOnTable} />
          ))}
        </div>
        {ended.length > 0 && (
          <>
            <div className="dir-field dir-field-gap">ENDED FIGHTS</div>
            <ul className="dir-ended" aria-label="Ended fights">
              {ended.map((e) => {
                const name = e.name || "Unnamed encounter";
                const owed = owes(e.id);
                return (
                  <li key={e.id} className="dir-ended-row" data-encounter={e.id}>
                    <span className="dir-scene-text">
                      <span className="dir-scene-name">{name}</span>
                      <span className={`dir-ended-sub${owed ? " is-owed" : ""}`}>{owed ? "Over · wear not committed" : "Over · wear committed"}</span>
                    </span>
                    {owed && (
                      <button type="button" className="dir-btn-ghost is-ink" aria-label={`Commit wear from ${name}`} disabled={act !== null} onClick={() => setAct({ id: e.id, kind: "wear" })}>
                        COMMIT WEAR
                      </button>
                    )}
                    <button type="button" className="dir-btn-ghost is-ink" aria-label={`Reopen ${name}`} disabled={act !== null} onClick={() => setAct({ id: e.id, kind: "reopen" })}>
                      REOPEN
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      <div className="dir-rule" />

      <section className="dir-sec" aria-label="Transmission">
        <SectionHead num="02" title="TRANSMISSION" note="Stays up until cleared." />
        <div role="radiogroup" aria-label="Transmission style" className="dir-tones">
          {TONES.map((t) => (
            <button key={t.tone} type="button" role="radio" aria-checked={t.tone === tone} className="dir-tone" onClick={() => setTone(t.tone)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="dir-hint">{TONES.find((t) => t.tone === tone)!.hint}</div>
        <label htmlFor="dir-tx" className="dir-field">
          MESSAGE
        </label>
        <textarea
          id="dir-tx"
          className="dir-textarea"
          rows={2}
          placeholder={SAMPLE_TRANSMISSION}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="dir-field">PREVIEW</div>
        <TonePreview tone={tone} text={trimmed || SAMPLE_TRANSMISSION} />
        <button type="button" className="dir-btn-send" disabled={trimmed === ""} onClick={transmit}>
          {live ? "REPLACE BANNER" : "TRANSMIT"}
        </button>
        {live && bannerTone(live) !== tone && <div className="dir-hint">Live now as {bannerTone(live).toUpperCase()}.</div>}
      </section>

      <div className="dir-rule" />

      <section className="dir-sec dir-sec-last" aria-label="Cues">
        <SectionHead num="03" title="CUES" note="One shot. Never replayed." />
        <div className="dir-cues">
          {CUES.map((cue) => (
            <button key={cue.type} type="button" className="dir-cue" aria-label={cue.aria} data-cue={cue.type} onClick={() => fire(cue)}>
              <span className="dir-cue-glyph">
                <CueGlyph type={cue.type} />
              </span>
              <span className="dir-cue-body">
                <span className="dir-cue-label">{cue.label}</span>
                <span className="dir-cue-blurb">{cue.blurb}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="dir-fired" aria-live="polite">
          {fired}
        </div>
      </section>

      {act !== null && (
        <EndedFightAct
          key={`${act.id}:${act.kind}`}
          act={act}
          roster={roster}
          actorId={session.actorId ?? "gm"}
          onDone={(ok) => {
            if (ok !== undefined) report(ok);
            setAct(null);
          }}
        />
      )}
      {endOpen && fight.state !== null && sceneEncounterIdOf(scene) !== null && (
        <div className="dir-ecp-host">
          <EndCombatPanel
            state={fight.state}
            roster={roster.state}
            compendium={compendium}
            encounterId={sceneEncounterIdOf(scene)!}
            actorId={session.actorId ?? "gm"}
            dispatch={roster.dispatch}
            onEnd={() => {
              const res = fight.end();
              if (res.ok) setEndOpen(false);
              return res;
            }}
            onClose={() => setEndOpen(false)}
          />
        </div>
      )}
      {notSent && (
        <div className="dir-notsent">
          <NotSentNote show />
        </div>
      )}
    </div>
  );
}

function SectionHead({ num, title, note }: { num: string; title: string; note: string }) {
  return (
    <div className="dir-head">
      <span className="dir-head-num">{num}</span>
      <span className="dir-head-title">{title}</span>
      <span className="dir-head-note">{note}</span>
    </div>
  );
}

/**
 * END COMBAT (opens the commit panel) while the fight is live; once it's over, REOPEN COMBAT, plus
 * COMMIT WEAR while its wear is off the roster. Disabled until the fight loads.
 */
function CombatControls({ fight, onEnd, reopen, commitWear }: { fight: FightStatus | null; onEnd: () => void; reopen: () => void; commitWear?: () => void }) {
  if (fight?.over) {
    return (
      <div className="dir-card-actions">
        {commitWear && (
          <button type="button" className="dir-btn-card is-dark" onClick={commitWear}>
            COMMIT WEAR
          </button>
        )}
        <button type="button" className="dir-btn-card" onClick={reopen}>
          REOPEN COMBAT
        </button>
      </div>
    );
  }
  return (
    <div className="dir-card-actions">
      <button type="button" className="dir-btn-card is-dark" disabled={fight === null} onClick={onEnd}>
        END COMBAT
      </button>
    </div>
  );
}

/** REOPEN or COMMIT WEAR on an ended fight, by its id. */
type EndedAct = { id: string; kind: "reopen" | "wear" };

/**
 * Runs one {@link EndedAct} over its own encounter socket (`useSceneFight`), mounted only while the
 * act is pending. REOPEN dispatches `reopen_combat` once the fight loads and reports; the server then
 * puts the fight on the table. COMMIT WEAR shows the END COMBAT panel as its commit-only gate (no
 * `onEnd`) until the commit lands or the GM cancels.
 */
function EndedFightAct({ act, roster, actorId, onDone }: { act: EndedAct; roster: UseRoster; actorId: string; onDone: (ok?: boolean) => void }) {
  const fight = useSceneFight(act.id);
  const { compendium } = useCompendiumContext();
  const loaded = fight.state !== null;
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (act.kind !== "reopen" || !loaded || sent) return;
    setSent(true);
    onDone(fight.reopen().ok);
  }, [act.kind, loaded, sent, fight, onDone]);
  if (act.kind !== "wear" || fight.state === null) return null;
  return (
    <div className="dir-ecp-host">
      <EndCombatPanel
        state={fight.state}
        roster={roster.state}
        compendium={compendium}
        encounterId={act.id}
        actorId={actorId}
        dispatch={roster.dispatch}
        onClose={() => onDone()}
      />
    </div>
  );
}

function sceneEncounterIdOf(scene: TableScene): string | null {
  return scene.kind === "encounter" ? scene.encounterId : null;
}

function SceneRow({
  scene,
  current,
  name,
  sub,
  kind,
  onPick,
}: {
  scene: TableScene;
  current: TableScene;
  name: string;
  sub: string;
  kind: string;
  onPick: (scene: TableScene) => void;
}) {
  const on = scene.kind === current.kind && (scene.kind === "narrative" || (current.kind === "encounter" && current.encounterId === scene.encounterId));
  return (
    <button type="button" role="radio" aria-checked={on} className="dir-scene" onClick={() => !on && onPick(scene)}>
      <span className="dir-radio">{on && <span className="dir-radio-pip" />}</span>
      <span className="dir-scene-text">
        <span className="dir-scene-name">{name}</span>
        <span className="dir-scene-sub">{sub}</span>
      </span>
      <span className="dir-scene-kind">{kind}</span>
    </button>
  );
}

/** A miniature of how the transmission lands on a player's screen, in the chosen tone. */
function TonePreview({ tone, text }: { tone: BannerTone; text: string }) {
  return (
    <div className="dir-preview" data-tone={tone} aria-hidden>
      {tone === "omninet" && (
        <div className="dir-pv-omni">
          <div className="dir-pv-omni-tag">OMNINET // TX</div>
          <div className="dir-pv-omni-text"><span className="dir-pv-line">{text}</span></div>
        </div>
      )}
      {tone === "alert" && (
        <div className="dir-pv-alert">
          <div className="dir-pv-hazard" />
          <div className="dir-pv-alert-text"><span className="dir-pv-line">{text}</span></div>
          <div className="dir-pv-hazard" />
        </div>
      )}
      {tone === "narration" && (
        <>
          <div className="dir-pv-lb-top" />
          <div className="dir-pv-lb-bot"><span className="dir-pv-line">{text}</span></div>
        </>
      )}
    </div>
  );
}

/** Each cue key's glyph, drawn in the key's dark head. */
function CueGlyph({ type }: { type: Cue["type"] }) {
  if (type === "glitch") {
    return (
      <svg width="56" height="40" viewBox="0 0 56 40" fill="none" strokeWidth="3">
        <path d="M4 8h30" stroke="#e9e6df" />
        <path d="M14 16h38" stroke="#ff4a2e" />
        <path d="M4 24h22" stroke="#e9e6df" />
        <path d="M20 32h30" stroke="#5fd6d0" />
      </svg>
    );
  }
  if (type === "impact") {
    return (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#e9e6df" strokeWidth="3">
        <circle cx="24" cy="24" r="6" fill="#e9e6df" />
        <path d="M24 3v8M24 37v8M3 24h8M37 24h8M9 9l6 6M33 33l6 6M39 9l-6 6M15 33l-6 6" />
      </svg>
    );
  }
  return (
    <svg width="56" height="40" viewBox="0 0 56 40" fill="none" strokeWidth="3">
      <rect x="16" y="8" width="24" height="24" stroke="#e9e6df" />
      <path d="M6 12v16M10 8v24M50 12v16M46 8v24" stroke="#ff4a2e" />
    </svg>
  );
}
